import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown, LayoutDashboard, LogOut } from "lucide-react";
import Logo from "./Logo";
import { useAuth } from "@/context/AuthContext";

const links = [
  { id: "about", label: "About" },
  { id: "products", label: "Products" },
  { id: "marketplace", label: "AI Marketplace" },
  { id: "industries", label: "Industries" },
  { id: "pricing", label: "Pricing" },
  { id: "blog", label: "Blog" },
  { id: "contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    handler();
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const goToSection = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "py-2" : "py-4"
      }`}
      data-testid="navbar"
    >
      <div className="container-px">
        <div className={`mx-auto max-w-7xl flex items-center justify-between rounded-full border border-white/8 ${
            scrolled ? "bg-black/70" : "bg-black/40"
          } backdrop-blur-xl px-4 md:px-6 py-2.5 transition-colors`}>
          <Link to="/" data-testid="nav-home-link"><Logo /></Link>

          <nav className="hidden lg:flex items-center gap-1">
            {links.map((l) => (
              <button
                key={l.id}
                onClick={() => goToSection(l.id)}
                data-testid={`nav-link-${l.id}`}
                className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/70 hover:text-gold-300 px-3 py-2 rounded-full transition-colors"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            {user ? (
              <div className="hidden md:flex items-center gap-2">
                <Link to="/dashboard" className="pill pill-ghost text-sm" data-testid="nav-dashboard-btn">
                  <LayoutDashboard className="h-4 w-4" /> Dashboard
                </Link>
                <button onClick={logout} className="pill pill-gold text-sm" data-testid="nav-logout-btn">
                  <LogOut className="h-4 w-4" /> Logout
                </button>
              </div>
            ) : (
              <div className="hidden md:flex items-center gap-2">
                <Link to="/login" className="pill pill-ghost text-sm" data-testid="nav-login-btn">Login</Link>
                <Link to="/register" className="pill pill-gold text-sm" data-testid="nav-register-btn">Get Started</Link>
              </div>
            )}

            <button
              className="lg:hidden p-2 text-white/80"
              onClick={() => setOpen((o) => !o)}
              data-testid="nav-mobile-toggle"
              aria-label="Toggle menu"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {open && (
          <>
            <div
              className="lg:hidden fixed inset-0 z-40 bg-black/85 backdrop-blur-md"
              onClick={() => setOpen(false)}
              aria-hidden
            />
            <div className="lg:hidden relative z-50 mt-2 mx-auto max-w-7xl glass-card !bg-ink-100 p-4 animate-fade-up" data-testid="mobile-menu">
            <div className="flex flex-col gap-1">
              {links.map((l) => (
                <button
                  key={l.id}
                  onClick={() => goToSection(l.id)}
                  className="text-left font-mono text-xs uppercase tracking-[0.18em] text-white/80 py-3 px-2 rounded-lg hover:bg-white/5"
                  data-testid={`mobile-nav-${l.id}`}
                >
                  {l.label}
                </button>
              ))}
              <div className="h-px bg-white/10 my-2" />
              {user ? (
                <>
                  <Link to="/dashboard" onClick={() => setOpen(false)} className="pill pill-ghost text-sm justify-center">Dashboard</Link>
                  <button onClick={() => { logout(); setOpen(false); }} className="pill pill-gold text-sm justify-center mt-2">Logout</button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setOpen(false)} className="pill pill-ghost text-sm justify-center">Login</Link>
                  <Link to="/register" onClick={() => setOpen(false)} className="pill pill-gold text-sm justify-center mt-2">Get Started</Link>
                </>
              )}
            </div>
          </div>
          </>
        )}
      </div>
    </header>
  );
}
